import React, { useMemo, useState, useEffect } from 'react';

// Generate random stars
const generateStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 5,
    duration: 2 + Math.random() * 3,
    opacity: 0.3 + Math.random() * 0.7,
  }));
};

// Generate shooting stars
const generateShootingStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    startX: Math.random() * 80 + 10,
    startY: Math.random() * 40,
    delay: Math.random() * 15 + i * 5,
    duration: 1 + Math.random() * 0.5,
  }));
};

export default function PokeverseSkyBackground() {
  const stars = useMemo(() => generateStars(150), []);
  const shootingStars = useMemo(() => generateShootingStars(3), []);
  const [isPetting, setIsPetting] = useState(false);
  const [isThundering, setIsThundering] = useState(false);

  // Random petting animation
  useEffect(() => {
    const triggerPetting = () => {
      setIsPetting(true);
      // Pet for 2 seconds
      setTimeout(() => setIsPetting(false), 2000);
      // Schedule next petting at random interval (8-20 seconds)
      setTimeout(triggerPetting, 8000 + Math.random() * 12000);
    };
    
    // Start after initial delay
    const initialTimeout = setTimeout(triggerPetting, 5000);
    return () => clearTimeout(initialTimeout);
  }, []);

  // Random thunder animation
  useEffect(() => {
    const triggerThunder = () => {
      setIsThundering(true);
      // Thunder flash lasts 500ms
      setTimeout(() => setIsThundering(false), 500);
      // Schedule next thunder at random interval (15-40 seconds)
      setTimeout(triggerThunder, 15000 + Math.random() * 25000);
    };
    
    // Start after initial delay
    const initialTimeout = setTimeout(triggerThunder, 10000);
    return () => clearTimeout(initialTimeout);
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Gradient sky background */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 80% 20%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 80%, rgba(88, 28, 135, 0.2) 0%, transparent 50%),
            linear-gradient(to bottom, 
              #0a0a0f 0%, 
              #0d1117 30%,
              #111827 60%,
              #0f172a 100%
            )
          `,
        }}
      />

      {/* Stars */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          {/* Star glow filter */}
          <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        <style>
          {`
            @keyframes twinkle {
              0%, 100% { opacity: 0.3; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.2); }
            }
            @keyframes shootingStar {
              0% { 
                transform: translateX(0) translateY(0);
                opacity: 1;
              }
              70% {
                opacity: 1;
              }
              100% { 
                transform: translateX(200px) translateY(200px);
                opacity: 0;
              }
            }
            @keyframes moonGlow {
              0%, 100% { 
                filter: drop-shadow(0 0 20px rgba(226, 232, 240, 0.3));
              }
              50% { 
                filter: drop-shadow(0 0 40px rgba(226, 232, 240, 0.5));
              }
            }
            @keyframes moonFloat {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-5px); }
            }
          `}
        </style>

        {/* Twinkling stars */}
        {stars.map((star) => (
          <circle
            key={star.id}
            cx={`${star.x}%`}
            cy={`${star.y}%`}
            r={star.size}
            fill="white"
            filter="url(#starGlow)"
            style={{
              opacity: star.opacity,
              animation: `twinkle ${star.duration}s ease-in-out infinite`,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}

        {/* Shooting stars */}
        {shootingStars.map((star) => (
          <g key={`shooting-${star.id}`}>
            <line
              x1={`${star.startX}%`}
              y1={`${star.startY}%`}
              x2={`${star.startX + 3}%`}
              y2={`${star.startY + 3}%`}
              stroke="url(#shootingGradient)"
              strokeWidth="2"
              strokeLinecap="round"
              style={{
                animation: `shootingStar ${star.duration}s ease-out infinite`,
                animationDelay: `${star.delay}s`,
                transformOrigin: `${star.startX}% ${star.startY}%`,
              }}
            />
          </g>
        ))}

        {/* Shooting star gradient */}
        <defs>
          <linearGradient id="shootingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Moon */}
      <div 
        className="absolute"
        style={{
          top: '5%',
          right: '10%',
          animation: 'moonFloat 8s ease-in-out infinite, moonGlow 4s ease-in-out infinite',
        }}
      >
        <svg width="140" height="140" viewBox="0 0 100 100">
          <defs>
            {/* Moon gradient */}
            <radialGradient id="moonGradient" cx="30%" cy="30%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="40%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </radialGradient>
            
            {/* Moon glow */}
            <filter id="moonGlowFilter" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          {/* Outer glow rings */}
          <circle cx="50" cy="50" r="48" fill="rgba(226, 232, 240, 0.05)" />
          <circle cx="50" cy="50" r="45" fill="rgba(226, 232, 240, 0.08)" />
          <circle cx="50" cy="50" r="42" fill="rgba(226, 232, 240, 0.12)" />
          
          {/* Moon body */}
          <circle 
            cx="50" 
            cy="50" 
            r="36" 
            fill="url(#moonGradient)"
            filter="url(#moonGlowFilter)"
          />
          
          {/* Moon craters - more detailed */}
          <circle cx="32" cy="38" r="7" fill="rgba(148, 163, 184, 0.3)" />
          <circle cx="55" cy="32" r="5" fill="rgba(148, 163, 184, 0.25)" />
          <circle cx="42" cy="58" r="9" fill="rgba(148, 163, 184, 0.2)" />
          <circle cx="65" cy="52" r="6" fill="rgba(148, 163, 184, 0.25)" />
          <circle cx="28" cy="55" r="4" fill="rgba(148, 163, 184, 0.2)" />
          <circle cx="58" cy="65" r="3" fill="rgba(148, 163, 184, 0.15)" />
          <circle cx="70" cy="38" r="3" fill="rgba(148, 163, 184, 0.2)" />
        </svg>
      </div>

      {/* Thunder flash effect from Pikachu */}
      <div 
        className={`absolute inset-0 pointer-events-none transition-opacity duration-100 ${isThundering ? 'opacity-100' : 'opacity-0'}`}
        style={{
          background: 'radial-gradient(ellipse at 85% 85%, rgba(250, 204, 21, 0.4) 0%, rgba(250, 204, 21, 0.2) 20%, transparent 50%)',
        }}
      />
      
      {/* Lightning bolts during thunder */}
      {isThundering && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
          {/* Main lightning bolt */}
          <svg 
            className="absolute" 
            style={{ 
              right: '5%', 
              top: '10%', 
              width: '200px', 
              height: '400px',
              filter: 'drop-shadow(0 0 20px #FBBF24) drop-shadow(0 0 40px #FCD34D) drop-shadow(0 0 60px rgba(250, 204, 21, 0.5))',
            }}
            viewBox="0 0 100 200"
          >
            {/* Main bolt - jagged lightning shape */}
            <path
              d="M50 0 L45 25 L55 30 L40 60 L52 65 L35 100 L50 105 L30 145 L55 150 L25 200"
              stroke="white"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Inner glow */}
            <path
              d="M50 0 L45 25 L55 30 L40 60 L52 65 L35 100 L50 105 L30 145 L55 150 L25 200"
              stroke="#FBBF24"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Branch 1 */}
            <path
              d="M45 25 L30 45 L20 55"
              stroke="white"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.8"
            />
            {/* Branch 2 */}
            <path
              d="M52 65 L70 75 L75 90"
              stroke="white"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.7"
            />
            {/* Branch 3 */}
            <path
              d="M35 100 L15 115 L10 130"
              stroke="white"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.6"
            />
          </svg>
          
          {/* Secondary smaller bolt */}
          <svg 
            className="absolute" 
            style={{ 
              right: '18%', 
              top: '5%', 
              width: '80px', 
              height: '180px',
              filter: 'drop-shadow(0 0 15px #FBBF24) drop-shadow(0 0 30px #FCD34D)',
              opacity: 0.7,
            }}
            viewBox="0 0 50 100"
          >
            <path
              d="M25 0 L22 15 L30 18 L18 40 L28 43 L15 70 L30 73 L10 100"
              stroke="white"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 15 L12 25"
              stroke="white"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.6"
            />
          </svg>
        </div>
      )}

      {/* Silhouette scene - Bottom right */}
      <div 
        className="absolute bottom-0 right-0 opacity-40"
        style={{ width: '500px', height: '350px' }}
      >
        <svg viewBox="0 0 300 200" className="w-full h-full" preserveAspectRatio="xMaxYMax meet">
          <style>
            {`
              @keyframes petMotion {
                0%, 100% { transform: translate(0, 0) rotate(0deg); }
                25% { transform: translate(5px, -3px) rotate(-5deg); }
                50% { transform: translate(8px, -5px) rotate(-8deg); }
                75% { transform: translate(5px, -3px) rotate(-5deg); }
              }
              @keyframes pikachuHappy {
                0%, 100% { transform: translateY(0); }
                25% { transform: translateY(-2px); }
                50% { transform: translateY(0); }
                75% { transform: translateY(-1px); }
              }
              @keyframes earWiggleLeft {
                0%, 100% { transform: rotate(0deg); }
                50% { transform: rotate(-5deg); }
              }
              @keyframes earWiggleRight {
                0%, 100% { transform: rotate(0deg); }
                50% { transform: rotate(5deg); }
              }
              @keyframes thunderGlow {
                0%, 100% { filter: drop-shadow(0 0 0 transparent); }
                50% { filter: drop-shadow(0 0 15px #FBBF24) drop-shadow(0 0 30px #FCD34D); }
              }
              .petting-arm {
                animation: ${isPetting ? 'petMotion 0.5s ease-in-out infinite' : 'none'};
                transform-origin: 10px 25px;
              }
              .pikachu-body {
                animation: ${isPetting ? 'pikachuHappy 0.3s ease-in-out infinite' : 'none'};
              }
              .pikachu-ear-left {
                animation: ${isPetting ? 'earWiggleLeft 0.4s ease-in-out infinite' : 'none'};
                transform-origin: -4px -6px;
              }
              .pikachu-ear-right {
                animation: ${isPetting ? 'earWiggleRight 0.4s ease-in-out infinite' : 'none'};
                transform-origin: 4px -6px;
              }
              .pikachu-glow {
                animation: ${isThundering ? 'thunderGlow 0.5s ease-in-out' : 'none'};
              }
            `}
          </style>
          
          {/* Tree branch */}
          <path
            d="M300 180 Q250 170 200 150 Q180 145 170 155 Q150 165 120 160 Q100 158 80 165 L80 200 L300 200 Z"
            fill="#0a0a0a"
          />
          
          {/* Tree trunk extending down-right */}
          <path
            d="M250 165 Q270 175 300 180 L300 200 L240 200 Q245 185 250 165"
            fill="#0a0a0a"
          />
          
          {/* Additional branch details */}
          <path
            d="M170 155 Q160 150 150 155 Q140 160 130 155"
            stroke="#0a0a0a"
            strokeWidth="4"
            fill="none"
          />
          
          {/* Ash silhouette sitting */}
          <g transform="translate(150, 100)">
            {/* Torso - slightly leaning forward toward Pikachu */}
            <path 
              d="M-12 22 Q-14 35 -10 48 L10 48 Q14 35 12 22 Q8 18 0 16 Q-8 18 -12 22" 
              fill="#0a0a0a" 
            />
            {/* Jacket collar detail */}
            <path d="M-8 20 Q0 24 8 20" stroke="#0a0a0a" strokeWidth="2" fill="none" />
            
            {/* Neck */}
            <rect x="-4" y="10" width="8" height="8" fill="#0a0a0a" />
            
            {/* Head - slightly tilted looking at Pikachu */}
            <ellipse cx="2" cy="2" rx="11" ry="12" fill="#0a0a0a" />
            
            {/* Spiky hair sticking out the back */}
            <path 
              d="M-10 -4 L-16 -8 L-12 -2 L-18 -3 L-13 1 L-17 4 L-11 3" 
              fill="#0a0a0a" 
            />
            {/* Hair spikes on sides */}
            <path d="M10 -2 L15 -5 L11 0" fill="#0a0a0a" />
            <path d="M10 2 L16 1 L11 4" fill="#0a0a0a" />
            
            {/* Cap - iconic Ash cap with front facing right */}
            <path 
              d="M-10 -6 Q-8 -14 2 -14 Q12 -14 14 -6 Q10 -8 2 -8 Q-6 -8 -10 -6" 
              fill="#0a0a0a" 
            />
            {/* Cap curved top */}
            <ellipse cx="2" cy="-14" rx="10" ry="4" fill="#0a0a0a" />
            {/* Cap brim - curved and pointing forward/right */}
            <path 
              d="M8 -8 Q16 -10 22 -6 Q18 -4 10 -4 Q8 -6 8 -8" 
              fill="#0a0a0a" 
            />
            {/* Cap button on top */}
            <circle cx="2" cy="-17" r="2" fill="#0a0a0a" />
            
            {/* Arm reaching towards Pikachu - animated */}
            <g className="petting-arm">
              {/* Upper arm */}
              <path 
                d="M10 24 Q16 22 20 18" 
                stroke="#0a0a0a" 
                strokeWidth="7" 
                fill="none" 
                strokeLinecap="round" 
              />
              {/* Forearm */}
              <path 
                d="M20 18 Q26 16 32 18" 
                stroke="#0a0a0a" 
                strokeWidth="6" 
                fill="none" 
                strokeLinecap="round" 
              />
              {/* Hand with fingers suggesting petting motion */}
              <ellipse cx="34" cy="18" rx="4" ry="3" fill="#0a0a0a" />
              <path d="M36 16 L38 14 M37 18 L40 17 M36 20 L38 21" stroke="#0a0a0a" strokeWidth="1.5" strokeLinecap="round" />
            </g>
            
            {/* Other arm resting on knee */}
            <path 
              d="M-10 28 Q-16 34 -16 42 Q-14 46 -12 44" 
              stroke="#0a0a0a" 
              strokeWidth="6" 
              fill="none" 
              strokeLinecap="round" 
            />
            {/* Resting hand */}
            <ellipse cx="-14" cy="44" rx="4" ry="3" fill="#0a0a0a" />
            
            {/* Legs - bent in sitting position */}
            <path 
              d="M-8 48 Q-14 54 -20 56 Q-22 58 -24 56 L-24 60 Q-20 62 -16 60 Q-8 56 -4 50" 
              fill="#0a0a0a" 
            />
            <path 
              d="M8 48 Q14 54 18 62 Q20 66 24 66 L28 66 Q26 62 22 58 Q14 50 12 48" 
              fill="#0a0a0a" 
            />
            {/* Shoes */}
            <ellipse cx="-24" cy="60" rx="5" ry="3" fill="#0a0a0a" />
            <ellipse cx="26" cy="66" rx="6" ry="3" fill="#0a0a0a" />
          </g>
          
          {/* Pikachu silhouette */}
          <g transform="translate(185, 125)" className="pikachu-body pikachu-glow">
            {/* Body */}
            <ellipse cx="0" cy="18" rx="12" ry="14" fill={isThundering ? '#1a1a1a' : '#0a0a0a'} />
            {/* Head */}
            <circle cx="0" cy="0" r="11" fill={isThundering ? '#1a1a1a' : '#0a0a0a'} />
            {/* Cheeks glowing during thunder */}
            {isThundering && (
              <>
                <circle cx="-8" cy="3" r="3" fill="#FBBF24" opacity="0.8" />
                <circle cx="8" cy="3" r="3" fill="#FBBF24" opacity="0.8" />
              </>
            )}
            {/* Left Ear - pointing outward */}
            <g className="pikachu-ear-left">
              <path d="M-6 -8 L-15 -26 L-3 -10 Z" fill={isThundering ? '#1a1a1a' : '#0a0a0a'} />
              {/* Ear tip (black part) */}
              <path d="M-15 -26 L-12 -20 L-10 -23 Z" fill="#050505" />
            </g>
            {/* Right Ear - pointing outward */}
            <g className="pikachu-ear-right">
              <path d="M6 -8 L15 -26 L3 -10 Z" fill={isThundering ? '#1a1a1a' : '#0a0a0a'} />
              {/* Ear tip (black part) */}
              <path d="M15 -26 L12 -20 L10 -23 Z" fill="#050505" />
            </g>
            {/* Tail - lightning bolt shape */}
            <path 
              d="M10 12 L22 5 L18 0 L28 -15 L20 -8 L24 -12 L14 2 L18 6 L12 14 Z" 
              fill={isThundering ? '#FBBF24' : '#0a0a0a'}
              style={isThundering ? { filter: 'drop-shadow(0 0 10px #FBBF24)' } : {}}
            />
            {/* Little feet */}
            <ellipse cx="-6" cy="30" rx="4" ry="3" fill="#0a0a0a" />
            <ellipse cx="6" cy="30" rx="4" ry="3" fill="#0a0a0a" />
          </g>
          
          {/* Small Pokemon (Caterpie-like) */}
          <g transform="translate(215, 145)">
            <ellipse cx="0" cy="5" rx="7" ry="5" fill="#0a0a0a" />
            <circle cx="-5" cy="2" r="4" fill="#0a0a0a" />
            <path d="M-7 -2 L-10 -7 M-4 -2 L-5 -7" stroke="#0a0a0a" strokeWidth="2" />
          </g>
        </svg>
      </div>

      {/* Subtle cloud/mist layers */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 opacity-20"
        style={{
          background: 'linear-gradient(to top, rgba(15, 23, 42, 0.8) 0%, transparent 100%)',
        }}
      />
      
      {/* Ambient particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/10"
            style={{
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

